import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MessageCircle, Link as LinkIcon, Send, AlertCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { useToast } from "../hooks/useToast";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(name, value, t) {
  switch (name) {
    case "name":
      if (!value.trim()) return t("contact.validation_name_required", "Name is required.");
      if (value.trim().length < 2) return t("contact.validation_name_min", "Name must be at least 2 characters.");
      return "";
    case "email":
      if (!value.trim()) return t("contact.validation_email_required", "Email is required.");
      if (!EMAIL_REGEX.test(value.trim())) return t("contact.validation_email_invalid", "Please enter a valid email address.");
      return "";
    case "message":
      if (!value.trim()) return t("contact.validation_message_required", "Message is required.");
      if (value.trim().length < 10) return t("contact.validation_message_min", "Message must be at least 10 characters.");
      return "";
    default:
      return "";
  }
}

export default function Contact() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({ name: "", email: "", message: "" });
  const [touched, setTouched] = useState({ name: false, email: false, message: false });
  const [sending, setSending] = useState(false);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (touched[field]) {
      setErrors((e) => ({ ...e, [field]: validateField(field, value, t) }));
    }
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((e) => ({ ...e, [field]: validateField(field, form[field], t) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", form.name, t),
      email: validateField("email", form.email, t),
      message: validateField("message", form.message, t),
    };
    setErrors(newErrors);
    setTouched({ name: true, email: true, message: true });

    if (newErrors.name || newErrors.email || newErrors.message) {
      addToast({
        type: "error",
        title: t("contact.error_title", "Missing fields"),
        description: t("contact.error_desc", "Please fill in all fields correctly."),
      });
      return;
    }

    setSending(true);
    setTimeout(() => {
      setSending(false);
      setForm({ name: "", email: "", message: "" });
      setErrors({ name: "", email: "", message: "" });
      setTouched({ name: false, email: false, message: false });
      addToast({
        type: "success",
        title: t("contact.success_title", "Message sent!"),
        description: t("contact.success_desc", "We'll get back to you within 2 hours."),
      });
    }, 800);
  };

  const inputClass = (field) =>
    `w-full bg-black border ${
      touched[field] && errors[field] ? "border-red-500" : "border-zinc-800"
    } focus:border-amber-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 px-4 py-3 text-white font-mono text-sm`;

  return (
    <>
      <SEOHead
        title={t("contact.seo_title", { defaultValue: "Contact | Talk to Our Team" })}
        description={t("contact.seo_desc", { defaultValue: "Get in touch with the Max Employee team. Schedule a demo, ask questions, or discuss custom enterprise solutions." })}
        url="/contact"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
            { "@type": "ListItem", position: 2, name: "Contact", item: "https://maxemployee.com/contact" }
            ]
          }
        ]}
      />
      <Header />
      <main id="main-content" className="min-h-screen bg-[#09090B]">
        <section className="relative overflow-hidden border-b border-zinc-900" aria-labelledby="contact-heading">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20">
            <Link to="/marketplace" className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-wider transition-colors mb-8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
              {t("contact.back_to_marketplace", "Back to Marketplace")}
            </Link>
            <h1 id="contact-heading" className="text-5xl md:text-6xl font-display font-bold text-white leading-[1.1] tracking-tight">{t('contact.talk_to_our_team')}</h1>
            <p className="mt-6 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-xl">
              {t("contact.hero_desc", "Questions about AI agents, pricing, or custom solutions? We typically respond within 2 hours during business hours.")}
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20" aria-labelledby="contact-methods-heading">
          <h2 id="contact-methods-heading" className="sr-only">{t('contact.contact_methods')}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Email */}
            <div className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors">
              <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-6" aria-hidden="true">
                <Mail className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className="font-display text-xl text-white uppercase mb-2">{t('contact.email_us')}</h3>
              <p className="text-zinc-400 text-sm mb-4">{t('contact.for_general_inquiries_and_support_questions')}</p>
              <a href="mailto:sales@maxemployee.com" className="text-amber-500 hover:text-amber-400 font-mono text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                sales@maxemployee.com
              </a>
            </div>

            {/* WhatsApp */}
            <div className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors">
              <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-6" aria-hidden="true">
                <MessageCircle className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className="font-display text-xl text-white uppercase mb-2">{t('referral.whatsapp')}</h3>
              <p className="text-zinc-400 text-sm mb-4">{t('contact.quick_questions_and_real_time_chat')}</p>
              <a href="https://wa.me/15551234567" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400 font-mono text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500" aria-live="polite">
                +1 (555) 123-4567
              </a>
            </div>

            {/* Enterprise */}
            <div className="border border-amber-500/30 rounded-xl p-8 bg-gradient-to-b from-amber-500/[0.03] to-transparent">
              <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mb-6" aria-hidden="true">
                <LinkIcon className="w-5 h-5 text-amber-500" />
              </div>
              <h3 className="font-display text-xl text-white uppercase mb-2">{t('contact.enterprise')}</h3>
              <p className="text-zinc-400 text-sm mb-4">{t('contact.custom_multi_agent_solutions_for_large')}</p>
              <a href="mailto:sales@maxemployee.com?subject=Enterprise Inquiry" className="text-amber-500 hover:text-amber-400 font-mono text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                {t("contact.request_demo", "Request a demo →")}
              </a>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12" aria-labelledby="contact-form-heading">
          <div className="max-w-2xl mx-auto border border-zinc-800 rounded-xl p-8 md:p-12">
            <h2 id="contact-form-heading" className="font-display text-2xl text-white uppercase mb-2">
              {t("contact.form_title", "Send us a message")}
            </h2>
            <p className="text-zinc-500 text-sm mb-8">
              {t("contact.form_subtitle", "Fill out the form below and we'll respond promptly.")}
            </p>
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label htmlFor="contact-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
                  {t("contact.name", "NAME")}
                </label>
                <input
                  id="contact-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  onBlur={() => handleBlur("name")}
                  className={inputClass("name")}
                  placeholder={t('contact.jane_doe')}
                  aria-invalid={touched.name && !!errors.name}
                  aria-describedby={touched.name && errors.name ? "contact-name-error" : undefined}
                />
                {touched.name && errors.name && (
                  <p id="contact-name-error" className="mt-2 text-red-400 text-xs font-mono flex items-center gap-1.5" role="alert">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="contact-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
                  {t("contact.email", "EMAIL")}
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                  className={inputClass("email")}
                  placeholder={t("contact.email_placeholder", "you@company.com")}
                  aria-invalid={touched.email && !!errors.email}
                  aria-describedby={touched.email && errors.email ? "contact-email-error" : undefined}
                />
                {touched.email && errors.email && (
                  <p id="contact-email-error" className="mt-2 text-red-400 text-xs font-mono flex items-center gap-1.5" role="alert">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    {errors.email}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="contact-message" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 block mb-2">
                  {t("contact.message", "MESSAGE")}
                </label>
                <textarea
                  id="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  onBlur={() => handleBlur("message")}
                  className={inputClass("message")}
                  placeholder={t("contact.message_placeholder", "Tell us about your project...")}
                  aria-invalid={touched.message && !!errors.message}
                  aria-describedby={touched.message && errors.message ? "contact-message-error" : undefined}
                />
                {touched.message && errors.message && (
                  <p id="contact-message-error" className="mt-2 text-red-400 text-xs font-mono flex items-center gap-1.5" role="alert">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    {errors.message}
                  </p>
                )}
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full group flex items-center justify-between bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black px-6 py-4 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
              >
                <span>{sending ? t("contact.sending", "Sending...") : t("contact.send_button", "Send message")}</span>
                {!sending && <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />}
              </button>
            </form>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12" aria-label={t('contact.response_time_information')}>
          <div className="border border-zinc-800 rounded-xl p-8 text-center">
            <p className="text-zinc-400 text-sm">
              <span className="text-white font-medium">{t('contact.response_time')}</span> {t('contact.response_time_detail', "Within 2 hours during business hours (Mon-Fri, 9am-6pm EST)")}
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
